/**
 * Auditoria 4 - Stage 4 - Item AOG UI en /cco.
 * Panel para visualizar eventos AOG activos con impacto (vuelos, pasajeros)
 * y aeronaves libres de reemplazo. Permite tomar la decision "Esperar" (nueva ETD)
 * o "Reemplazar aeronave" por vuelo afectado.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { UTCDateTimePicker } from '../ui/utc-datetime-picker';
import { AlertTriangle, Plane, Users, Clock, RefreshCw, ArrowRight, Wrench } from 'lucide-react';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
    } catch { return iso; }
}

function StatusBadge({ status }) {
    const map = {
        open: { label: 'Abierto', cls: 'bg-red-500/15 text-red-500 border-red-500/30' },
        in_repair: { label: 'En reparación', cls: 'bg-amber-500/15 text-amber-500 border-amber-500/30' },
        resolved: { label: 'Resuelto', cls: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30' },
        closed: { label: 'Cerrado', cls: 'bg-slate-500/15 text-slate-400 border-slate-500/30' },
    };
    const s = map[status] || map.open;
    return <Badge variant="outline" className={s.cls}>{s.label}</Badge>;
}

export default function AOGPanel({ token }) {
    const headers = { Authorization: `Bearer ${token}` };
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEventId, setSelectedEventId] = useState(null);
    const [impact, setImpact] = useState(null);
    const [impactLoading, setImpactLoading] = useState(false);
    // Decision dialog state
    const [decisionFlight, setDecisionFlight] = useState(null);
    const [decisionMode, setDecisionMode] = useState('wait'); // 'wait' | 'swap_aircraft'
    const [newEtd, setNewEtd] = useState('');
    const [newAircraftId, setNewAircraftId] = useState('');
    const [savingDecision, setSavingDecision] = useState(false);

    const fetchEvents = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/aog`, { headers });
            // Bug fix: solo eventos realmente abiertos (status='open').
            // 'resolved' NO es 'closed' pero tampoco es activo -> no debe aparecer aca.
            const list = (res.data || []).filter(e => (e.status || 'open') === 'open');
            setEvents(list);
        } catch (e) {
            toast.error('Error al cargar eventos AOG');
        } finally { setLoading(false); }
    }, [token]);

    const fetchImpact = useCallback(async (eventId) => {
        if (!eventId) return;
        setImpactLoading(true);
        try {
            const res = await axios.get(`${API}/aog/${eventId}/impact`, { headers });
            setImpact(res.data);
        } catch (e) {
            toast.error('Error al cargar impacto');
        } finally { setImpactLoading(false); }
    }, [token]);

    useEffect(() => { fetchEvents(); }, [fetchEvents]);
    useAutoRefresh(fetchEvents, 30000);

    useEffect(() => {
        if (selectedEventId) fetchImpact(selectedEventId);
        else setImpact(null);
    }, [selectedEventId, fetchImpact]);

    const openDecision = (flight, mode) => {
        setDecisionFlight(flight);
        setDecisionMode(mode);
        setNewEtd('');
        setNewAircraftId('');
    };

    const submitDecision = async () => {
        if (!decisionFlight || !selectedEventId) return;
        if (decisionMode === 'wait' && !newEtd) {
            toast.error('Ingresa la nueva ETD');
            return;
        }
        if (decisionMode === 'swap_aircraft' && !newAircraftId) {
            toast.error('Selecciona una aeronave de reemplazo');
            return;
        }
        setSavingDecision(true);
        try {
            const body = {
                decision: decisionMode,
                flight_id: decisionFlight.id,
            };
            if (decisionMode === 'wait') body.new_etd = newEtd; // UTCDateTimePicker ya emite ISO UTC
            if (decisionMode === 'swap_aircraft') body.new_aircraft_id = newAircraftId;
            await axios.post(`${API}/aog/${selectedEventId}/decision`, body, { headers });
            toast.success(decisionMode === 'wait' ? 'Demora registrada y notificada' : 'Aeronave reemplazada y notificada');
            setDecisionFlight(null);
            fetchImpact(selectedEventId);
            fetchEvents();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al registrar la decisión');
        } finally { setSavingDecision(false); }
    };

    // Auditoria 6 v2: 'Actualizar ETR' movido al módulo de Mantenimiento (ya no vive en /cco).

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Cargando eventos AOG...
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(280px,340px)_1fr] gap-4" data-testid="aog-panel">
            {/* Lista de eventos */}
            <Card className="card-aviation">
                <CardHeader className="pb-2">
                    <CardTitle className="font-['Barlow_Condensed'] uppercase tracking-tight text-base flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        Eventos AOG Activos
                        <Badge className="ml-auto bg-red-500/15 text-red-500 border-red-500/30" variant="outline">{events.length}</Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1.5 max-h-[560px] overflow-y-auto">
                    {events.length === 0 && (
                        <p className="text-xs text-muted-foreground py-6 text-center">
                            Sin eventos AOG abiertos. La operación fluye normal.
                        </p>
                    )}
                    {events.map(ev => {
                        const active = ev.id === selectedEventId;
                        return (
                            <button
                                key={ev.id}
                                onClick={() => setSelectedEventId(ev.id)}
                                className={`w-full text-left p-2.5 rounded-lg border transition-colors ${active ? 'bg-red-500/10 border-red-500/40' : 'bg-muted/20 border-border/30 hover:bg-muted/40'}`}
                                data-testid={`aog-event-${ev.id}`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <Plane className="w-3.5 h-3.5 text-red-500 shrink-0" />
                                        <span className="font-medium text-sm truncate">{ev.aircraft_registration || 'A/C ?'}</span>
                                        {ev.station && <span className="text-xs text-muted-foreground truncate">· {ev.station}</span>}
                                    </div>
                                    <StatusBadge status={ev.status || 'open'} />
                                </div>
                                <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{ev.work_description || 'Sin descripción'}</p>
                                <div className="flex items-center gap-1 text-[10px] text-muted-foreground mt-1">
                                    <Clock className="w-3 h-3" />
                                    ETR: {fmtDate(ev.etr)}
                                </div>
                            </button>
                        );
                    })}
                </CardContent>
            </Card>

            {/* Detalle de impacto */}
            <Card className="card-aviation">
                <CardHeader className="pb-2">
                    <CardTitle className="font-['Barlow_Condensed'] uppercase tracking-tight text-base flex items-center gap-2">
                        <Wrench className="w-4 h-4 text-primary" />
                        Impacto Operacional
                        {impact?.affected_flights && (
                            <span className="ml-auto text-xs text-muted-foreground font-normal">
                                {impact.affected_flights.length} vuelo(s) · {impact.affected_passengers?.total_pax || 0} pax
                            </span>
                        )}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {!selectedEventId && (
                        <p className="text-xs text-muted-foreground py-10 text-center">
                            Seleccioná un evento AOG de la izquierda para ver el impacto.
                        </p>
                    )}
                    {selectedEventId && impactLoading && (
                        <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Cargando impacto...
                        </div>
                    )}
                    {selectedEventId && impact && !impactLoading && (
                        <>
                            {/* Encabezado evento */}
                            <div className="rounded-lg bg-muted/30 border border-border/40 p-3 space-y-1">
                                <div className="flex items-center justify-between">
                                    <div className="text-sm font-medium">{impact.event?.aircraft_registration}</div>
                                    <StatusBadge status={impact.event?.status || 'open'} />
                                </div>
                                <p className="text-xs text-muted-foreground">{impact.event?.work_description}</p>
                                <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                                    <span>Estación: {impact.event?.station || '—'}</span>
                                    <span>ETR: {fmtDate(impact.event?.etr)}</span>
                                </div>
                            </div>

                            {/* Vuelos afectados */}
                            <div>
                                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
                                    <Plane className="w-3.5 h-3.5" /> Vuelos afectados
                                </div>
                                {impact.affected_flights.length === 0 && (
                                    <p className="text-xs text-muted-foreground italic">Sin vuelos programados antes del ETR.</p>
                                )}
                                <div className="space-y-2">
                                    {impact.affected_flights.map(f => {
                                        const paxOnFlight = (impact.affected_passengers.reservations || [])
                                            .filter(r => r.flight_id === f.id)
                                            .reduce((a, r) => a + (r.pax_count || 1), 0);
                                        return (
                                            <div key={f.id} className="rounded-lg border border-border/40 bg-background/50 p-2.5" data-testid={`aog-affected-flight-${f.id}`}>
                                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <span className="font-medium">{f.flight_number}</span>
                                                        <span className="text-muted-foreground">{f.origin}</span>
                                                        <ArrowRight className="w-3 h-3 text-muted-foreground" />
                                                        <span className="text-muted-foreground">{f.destination}</span>
                                                        <span className="text-xs text-muted-foreground ml-2">{fmtDate(f.scheduled_departure)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Badge variant="outline" className="text-[10px]">
                                                            <Users className="w-3 h-3 mr-1" />{paxOnFlight} pax
                                                        </Badge>
                                                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openDecision(f, 'wait')} data-testid={`aog-wait-${f.id}`}>
                                                            Esperar
                                                        </Button>
                                                        <Button size="sm" className="h-7 text-xs btn-primary" onClick={() => openDecision(f, 'swap_aircraft')} disabled={(impact.replacement_aircraft || []).length === 0} data-testid={`aog-swap-${f.id}`}>
                                                            Reemplazar A/C
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Aeronaves de reemplazo */}
                            <div>
                                <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
                                    <RefreshCw className="w-3.5 h-3.5" /> Aeronaves libres de reemplazo
                                </div>
                                {(impact.replacement_aircraft || []).length === 0 ? (
                                    <p className="text-xs text-muted-foreground italic">No hay aeronaves libres en la ventana AOG.</p>
                                ) : (
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                        {impact.replacement_aircraft.map(ac => (
                                            <div key={ac.id} className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-2" data-testid={`aog-replacement-${ac.id}`}>
                                                <div className="text-sm font-medium">{ac.registration}</div>
                                                <div className="text-[11px] text-muted-foreground">{ac.aircraft_type || ac.model || '—'}</div>
                                                {ac.base_airport && <div className="text-[10px] text-muted-foreground">Base: {ac.base_airport}</div>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Dialogo decisión */}
            <Dialog open={!!decisionFlight} onOpenChange={() => setDecisionFlight(null)}>
                <DialogContent className="sm:max-w-[440px]">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] uppercase text-lg flex items-center gap-2">
                            <Wrench className="w-4 h-4 text-primary" />
                            {decisionMode === 'wait' ? 'Esperar reparación' : 'Reemplazar aeronave'}
                        </DialogTitle>
                        <DialogDescription>
                            {decisionFlight?.flight_number} · {decisionFlight?.origin} → {decisionFlight?.destination}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        {decisionMode === 'wait' ? (
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Nueva ETD (UTC)</label>
                                <UTCDateTimePicker value={newEtd} onChange={setNewEtd} testid="aog-decision-etd" />
                                <p className="text-[11px] text-muted-foreground">Se emitirá un aviso de demora a CCO/Despacho/Admin.</p>
                            </div>
                        ) : (
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Aeronave de reemplazo</label>
                                <Select value={newAircraftId} onValueChange={setNewAircraftId}>
                                    <SelectTrigger data-testid="aog-decision-aircraft"><SelectValue placeholder="Selecciona aeronave" /></SelectTrigger>
                                    <SelectContent>
                                        {(impact?.replacement_aircraft || []).map(ac => (
                                            <SelectItem key={ac.id} value={ac.id}>
                                                {ac.registration} — {ac.aircraft_type || ac.model || '—'}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDecisionFlight(null)}>Cancelar</Button>
                        <Button className="btn-primary" onClick={submitDecision} disabled={savingDecision} data-testid="aog-decision-submit">
                            {savingDecision && <RefreshCw className="w-3.5 h-3.5 mr-2 animate-spin" />}
                            Confirmar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Auditoria 6 v2: dialogo 'Actualizar ETR' migrado a MaintenancePage.js */}
        </div>
    );
}
